import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import LinkButton from 'flarum/common/components/LinkButton';
import ItemList from 'flarum/common/utils/ItemList';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import Footer from 'flarum/forum/components/Footer';
import UserPage from 'flarum/forum/components/UserPage';
import SessionDropdown from 'flarum/forum/components/SessionDropdown';
import { HomePage, InfoPage } from './components/PublicPages';
import { OrdersPage, DashboardPage } from './components/OrdersPage';
import NewOrderPage from './components/NewOrderPage';
import OrderPage from './components/OrderPage';
import ProfilePage from './components/ProfilePage';
import ChangePasswordModal from './components/ChangePasswordModal';
import { BsiFooter } from './components/Shared';
import { buttonIcon } from './components/BsiIcon';
import polishAuth from './polishAuth';
import configureHomeTitle from './homeTitle';
import { t, canManage } from './helpers';
app.initializers.add('bsi-orders', () => {
  polishAuth();
  configureHomeTitle();
  app.routes.index = {
    path: '/',
    component: HomePage
  };
  app.routes['bsi.home'] = {
    path: '/home',
    component: HomePage
  };
  app.routes['bsi.dashboard'] = {
    path: '/dashboard',
    component: DashboardPage
  };
  app.routes['bsi.orders'] = {
    path: '/orders',
    component: OrdersPage
  };
  app.routes['bsi.new'] = {
    path: '/orders/new',
    component: NewOrderPage
  };
  app.routes['bsi.order'] = {
    path: '/orders/:id',
    component: OrderPage
  };
  app.routes['bsi.admin-orders'] = {
    path: '/manage/orders',
    component: OrdersPage,
    params: {
      admin: true
    }
  };
  // Route params are supplied through a dedicated component to work with all Flarum resolvers.
  app.routes['bsi.admin-orders'].component = class extends OrdersPage {
    oninit(vnode) {
      vnode.attrs.admin = true;
      super.oninit(vnode);
    }
  };
  app.routes['bsi.profile'] = {
    path: '/profile',
    component: ProfilePage
  };
  app.routes.user = {
    path: '/u/:username',
    component: ProfilePage
  };
  delete app.routes['user.posts'];
  delete app.routes['user.discussions'];
  delete app.routes.discussion;
  delete app.routes['discussion.near'];
  delete app.routes.posts;
  for (const section of ['services', 'payment', 'refund', 'terms', 'contacts']) {
    app.routes['bsi.' + section] = {
      path: '/' + section,
      component: class extends InfoPage {
        oninit(vnode) {
          vnode.attrs.section = section;
          super.oninit(vnode);
        }
      }
    };
  }
  extend(HeaderPrimary.prototype, 'items', function (items) {
    if (!app.session.user) return;
    [['dashboard', 'house'], ['orders', 'folders'], ['new_order', 'plus-circle'], ['profile', 'user-circle']].forEach((_ref, index) => {
      let [key, icon] = _ref;
      const route = key === 'new_order' ? 'bsi.new' : 'bsi.' + key;
      items.add('bsi-' + key, m(LinkButton, {
        className: "Button Button--link",
        href: app.route(route),
        icon: buttonIcon(icon)
      }, t('nav.' + key)), 100 - index * 10);
    });
    if (canManage()) items.add('bsi-admin', m(LinkButton, {
      className: "Button Button--link",
      href: app.route('bsi.admin-orders'),
      icon: buttonIcon('list-checks')
    }, t('nav.admin_orders')), 20);
  });
  extend(HeaderSecondary.prototype, 'items', items => {
    items.remove('search');
    items.remove('notifications');
    for (const [item, icon] of [['signUp', 'user-plus'], ['logIn', 'sign-in']]) {
      if (!items.has(item)) continue;
      const button = items.get(item);
      button.attrs.icon = buttonIcon(icon);
      button.attrs.className += ' BsiHeaderAuth BsiHeaderAuth--' + item;
    }
  });
  override(Footer.prototype, 'view', () => m(BsiFooter, null));
  override('flarum/forum/components/LogInModal', 'footer', function () {
    return app.forum.attribute('allowSignUp') && m("p", {
      className: "LogInModal-signUp"
    }, app.translator.trans('core.forum.log_in.sign_up_text', {
      a: m(Button, {
        className: "Button Button--text Button--link",
        onclick: this.signUp.bind(this)
      })
    }));
  });
  override(UserPage.prototype, 'navItems', function () {
    const items = new ItemList();
    const isSelf = this.user && app.session.user?.id() === this.user.id();
    items.add('bsi-profile', m(LinkButton, {
      href: isSelf ? app.route('bsi.profile') : app.route('user', {
        username: this.user.slug()
      }),
      icon: buttonIcon('user-circle')
    }, t('nav.profile')), 100);
    if (isSelf) items.add('settings', m(LinkButton, {
      href: app.route('settings'),
      icon: buttonIcon('gear')
    }, t('profile.settings')), 90);
    if (isSelf || app.forum.attribute('canModerateAccessTokens')) items.add('security', m(LinkButton, {
      href: app.route('user.security', {
        username: this.user.slug()
      }),
      icon: buttonIcon('shield-check')
    }, app.translator.trans('core.forum.user.security_link')), 80);
    return items;
  });
  extend(SessionDropdown.prototype, 'items', items => {
    items.add('profile', m(LinkButton, {
      icon: buttonIcon('user-circle'),
      href: app.route('bsi.profile')
    }, t('nav.profile')), 100);
    if (canManage()) items.add('bsi-admin', m(LinkButton, {
      href: app.route('bsi.admin-orders'),
      icon: buttonIcon('list-checks')
    }, t('nav.admin_orders')), 90);
  });
  extend('flarum/common/components/EditUserModal', 'fields', items => {
    items.remove('email');
  });
  extend('flarum/common/components/EditUserModal', 'data', data => {
    delete data.email;
  });
  extend('flarum/forum/components/SettingsPage', 'accountItems', items => {
    items.remove('changeEmail');
    items.remove('changePassword');
    items.add('bsi-change-password', m(Button, {
      className: "Button",
      icon: buttonIcon('lock-key'),
      onclick: () => app.modal.show(ChangePasswordModal)
    }, t('profile.change_password')), 100);
  });
  extend('flarum/forum/components/SettingsPage', 'settingsItems', items => {
    items.remove('notifications');
    items.remove('privacy');
  });
}, -10);